import React, { useEffect, useReducer, useState } from "react";
import { Frame } from '@shopify/polaris';
import { useQuery, useMutation, NetworkStatus, ApolloQueryResult, FetchResult } from "@apollo/client";

import Content from '../components/order/Content';
import { NoOrderData } from '../components/Helpers';
import {
  UpdateOrderMetafieldsResponse,
  updOrderMetafieldsMutation,
  updOrderShipDateOpts,
  updOrderStatusOpts
} from "../queries/shopify/updOrderMetafields";
import {
  getOrderShipDate,
  getOrderStatus,
  getOrderTagsAndAttributes,
  mapOrderDataToObject,
  normalizeShipDateObjectDate,
} from '../utils/order/orderData';
import { LIVEART_CUSTOM_DESIGN, Manufacturer } from '../utils/types';
import { OrderStatus } from '../models/orderStatus';
import { ShipDate } from '../models/shipDate';
import { Item } from '../models/item';
import { useFetchOrder } from "../queries/shopify/useFetchOrder";
import { Order as OrderModel } from "../models/order";

const Order = ({ orderId }: { orderId: string }) => {
  // region 1) Fetching order data and items
  const fetchOrder = useFetchOrder(orderId);
  // region Setup
  const [order, setOrder] = useState<OrderModel | null>(null);

  useEffect(() => {
    if (fetchOrder.status === "ready") {
      setOrder(mapOrderDataToObject(fetchOrder.orderData));
    }
  }, [fetchOrder.status])

  // mutation to update metafields (SendStatus and ShipDate)
  const [setMetafield] = useMutation<UpdateOrderMetafieldsResponse>(updOrderMetafieldsMutation);

  if (fetchOrder.status === 'loading')
    return <NoOrderData loading title="Loading order..." />;

  if (fetchOrder.status === 'error' || !fetchOrder.orderData)
    return (
      <NoOrderData
        title="Cannot access order data"
        msg={
          'The order either does not exist or is over 60 days old ' +
          'and cannot be accessed by the application.'
        }
      />
    );

  // endregion

  if (order && (!order.items || !order.items.length)) {
    return (
      <NoOrderData
        title="Nothing to display"
        msg={`This order does not contain any ${LIVEART_CUSTOM_DESIGN} items`}
      />
    );
  }
  // endregion

  // region Handlers to update Send to Manufacturer Status & Ship Date & Vendor
  const  updateSendManStatusHandler = (
    newStatusObject: OrderStatus,
    manufacturers: Manufacturer[],
    runUpdateOrderHandler = true,
  ) => {
    setMetafield({
      ...updOrderStatusOpts({
        orderId,
        metaId: 'id' in order.sendManStatus ? order.sendManStatus.id : undefined,
        statusObject: newStatusObject,
        manufacturers,
        oldAttributes: order.customAttributes,
        oldTags: order.tags,
      }),
    }).then(async data => {
      if (runUpdateOrderHandler) await fetchOrder.refetchOrder();
      refreshOrderEditableFields(data);
    });
  }

  const  updateSendManShipDateHandler = (newShipDateObject: ShipDate) => {
    setMetafield({
      ...updOrderShipDateOpts({
        orderId,
        metaId: order.sendManShipDate ? order.sendManShipDate.id : undefined,
        shipDateObject: normalizeShipDateObjectDate(newShipDateObject),
        oldAttributes: order.customAttributes,
        oldTags: order.tags,
      }),
    }).then(data => refreshOrderEditableFields(data));
  }

  const refreshOrderEditableFields = (mutationResult: FetchResult<UpdateOrderMetafieldsResponse>) => {
    setOrder({
      ...order,
      sendManStatus: getOrderStatus(
        mutationResult.data
          ? mutationResult.data.orderUpdate.order
          : fetchOrder.orderData,
      ),
      sendManShipDate: getOrderShipDate(
        mutationResult.data
          ? mutationResult.data.orderUpdate.order
          : fetchOrder.orderData,
      ),
      ...getOrderTagsAndAttributes(
        mutationResult.data
          ? mutationResult.data.orderUpdate.order
          : fetchOrder.orderData,
      ),
    })
  }

  const setVendor = (productId: string, vendor: Manufacturer) => {
    const index = order.items.findIndex((item: Item) => item.id === productId);
    const oldItem = order.items[index];
    const newItem: Item = {
      ...oldItem, product: {
        ...oldItem.product,
        vendor
      }
    };
    const items = [...order.items];
    items[index] = newItem;
    setOrder({ ...order, items });
  }

  // endregion

  return (
    <Frame>
      {!order ? (
        <NoOrderData loading title="Loading group..." />
      ) : (
        <Content
          order={order}
          saveStatusHandler={updateSendManStatusHandler}
          saveShipDateHandler={updateSendManShipDateHandler}
          setVendor={setVendor}
        />
      )}
    </Frame>
  );
};



export default Order;
